/**
 * Generic, domain-agnostic pagination helpers.
 *
 * One implementation of "page through every row" + row extraction + silent-
 * truncation telemetry, reused by every list domain (schedules, participants,
 * and any future endpoint) so screens never hand-roll fetch loops or guess a
 * large `limit`.
 *
 * A "list response" is assumed to be an axios-style `{ data }` where `data` is
 * either a raw array or `{ [rowsKey]: [], pagination: { total, pages, ... } }`.
 */

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

/** Pull the rows array out of a list response body regardless of legacy/array shape. */
export const extractRows = (data: unknown, rowsKey: string): any[] => {
  if (Array.isArray(data)) return data;
  const rows = (data as Record<string, unknown> | null | undefined)?.[rowsKey];
  return Array.isArray(rows) ? rows : [];
};

/**
 * Warn when the backend reports more total rows than were returned in a single
 * page — the signature of silent truncation (the bug large hardcoded limits
 * caused). Read-only/no behaviour change.
 */
export const reportTruncationIfAny = (
  data: unknown,
  rowsKey: string,
  context: string,
): void => {
  const total = (data as { pagination?: { total?: number } } | null | undefined)
    ?.pagination?.total;
  const returned = extractRows(data, rowsKey).length;
  if (typeof total === 'number' && total > returned) {
    // eslint-disable-next-line no-console
    console.warn(
      `[paginateAll] possible truncation in "${context}": received ${returned} of ${total} rows. ` +
        `Use page/limit or the listAll helper instead of a single large limit.`,
    );
  }
};

type ListFn = (params: Record<string, unknown>) => Promise<{ data?: unknown }>;

/**
 * Fetch every row matching `params` by paging through `listFn` in `pageSize`
 * chunks. Page 1 is fetched first (to learn the page count), then the remaining
 * pages are fetched in parallel with bounded concurrency — much faster than
 * serial for large sets, while the cap avoids hammering the backend. Throws on
 * the first failed page so callers never silently render a partial set.
 */
export const paginateAll = async (
  listFn: ListFn,
  params: Record<string, unknown>,
  rowsKey: string,
  pageSize: number,
): Promise<{rows: any[]; pagination: PaginationMeta}> => {
  const baseParams = {...params, limit: pageSize};

  const firstRes = await listFn({...baseParams, page: 1});
  const firstData = firstRes?.data ?? {};
  const firstRows = extractRows(firstData, rowsKey);
  const total: number =
    (firstData as {pagination?: {total?: number}})?.pagination?.total ??
    firstRows.length;
  const pages: number =
    (firstData as {pagination?: {pages?: number}})?.pagination?.pages ?? 1;

  if (pages <= 1) {
    return {rows: firstRows, pagination: {total, pages, page: 1, limit: pageSize}};
  }

  // Fetch the remaining pages with bounded concurrency (much faster than serial
  // for large sets) while preserving order. The cap keeps us from hammering the
  // backend with one request per page on very large projects.
  const CONCURRENCY = 5;
  const pageRows: any[][] = new Array(pages + 1);
  pageRows[1] = firstRows;
  const queue: number[] = [];
  for (let p = 2; p <= pages; p++) queue.push(p);

  let next = 0;
  const worker = async () => {
    while (next < queue.length) {
      const page = queue[next++];
      const res = await listFn({...baseParams, page});
      pageRows[page] = extractRows(res?.data ?? {}, rowsKey);
    }
  };
  await Promise.all(
    Array.from({length: Math.min(CONCURRENCY, queue.length)}, worker),
  );

  const rows: any[] = [];
  for (let p = 1; p <= pages; p++) {
    if (pageRows[p]) rows.push(...pageRows[p]);
  }

  return {rows, pagination: {total, pages, page: 1, limit: pageSize}};
};
